import {
	TreatmentPlan,
	TreatmentPlanRequest,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import { readTemplateSegments } from "../../components/SegmentEditor/segmentEditor.helpers";

/**
 * SIMS-9027 (FR-5, FR-16) — did this save actually move the disenrollment threshold?
 *
 * Mirrors the backend helper of the same name. Stored-value equality is the wrong test in both
 * directions:
 *
 *   unset -> 30    NO change  — 30 was already in force via the runtime fallback
 *   14    -> unset IS a change — the effective value moves from 14 to 30
 *
 * so the comparison is on the EFFECTIVE value. The frontend needs its own copy because it decides
 * whether to show the confirmation at all; the backend re-verifies before writing anything, so this
 * is a UX gate rather than the authority.
 */

/** Mirrors the `|| 30` fallback in resolveDisenrollmentThreshold (backend). */
export const PLATFORM_DEFAULT_DISENROLLMENT_DAYS = 30;

export const effectiveThreshold = (stored: number | null | undefined): number =>
	stored || PLATFORM_DEFAULT_DISENROLLMENT_DAYS;

export interface EffectiveThresholdChange {
	templateName: string;
	/**
	 * SIMS-9041 — the template's stable id, when the plan has one. Sent with the name so the server
	 * can resolve the cohort by identity: a rename invalidates the label, not the id.
	 */
	templateId?: string;
	/**
	 * WHICH threshold moved (FR-8). A segment change writes into that phase of each participant's
	 * snapshot rather than the template default, so someone still in an earlier phase carries the
	 * corrected value for when they reach it (AC4 / FR-18).
	 */
	field: "TEMPLATE_DEFAULT" | "SEGMENT";
	/** Present only for a segment change: the phase's weeks as saved, for the copy and as a fallback key. */
	segmentWeekRange?: { start: number; end: number };
	/**
	 * SIMS-9223 — the phase's stable id, when it has one. The server finds each participant's phase
	 * by this id first, so a save that moves the phase's weeks still reaches everyone in it.
	 */
	segmentId?: string;
	/** 1-based, for the confirmation copy: "segment 2 (weeks 7-12)". */
	segmentNumber?: number;
	previousValue: number;
	nextValue: number;
}

/**
 * SIMS-9223 — the phase in `previous` (the saved plan) that `next` (this save) is.
 *
 *   - the saved list carries ids -> by id ONLY. The editor round-trips every saved phase's id, so a
 *     phase in this save WITHOUT one was added in this save: it is new, even on the weeks of a phase
 *     that was removed, and is never re-bound to that phase by its weeks (the backend's rule for an
 *     id that matches nothing, segment-identity.helper.ts);
 *   - the saved list carries no ids (a legacy list, or one saved before ids existed) -> by week
 *     range, as before.
 * Matching by id is what lets a save that changes a phase's weeks AND its threshold still be seen
 * as a change to that phase.
 */
const findPreviousSegment = (
	previousSegments: TreatmentSegment[],
	nextSegment: TreatmentSegment,
): TreatmentSegment | undefined => {
	if (previousSegments.some((segment) => segment.segmentId)) {
		return nextSegment.segmentId
			? previousSegments.find(
					(segment) => segment.segmentId === nextSegment.segmentId,
				)
			: undefined;
	}
	return previousSegments.find(
		(segment) =>
			segment.weekRange?.start === nextSegment.weekRange?.start &&
			segment.weekRange?.end === nextSegment.weekRange?.end,
	);
};

/**
 * The first template whose effective threshold moved, or null when none did.
 *
 * Returns at most ONE change: the confirmation is per propagating save and the epic's v1 scope is
 * the template default (FR-24 gates anything wider). Templates are matched by NAME because they
 * carry no id — the same missing identity SIMS-9041 is about.
 *
 * Returns null when the plan has not opted in (FR-2/FR-16): the option alone changes nothing, and
 * a plan that never set it behaves exactly as it does today (FR-3).
 */
export const findEffectiveThresholdChange = (
	previous: TreatmentPlan,
	next: TreatmentPlanRequest,
): EffectiveThresholdChange | null => {
	if (!next.propagateThresholdToCurrentEnrollments) {
		return null;
	}

	const previousTemplates =
		previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
	const nextTemplates =
		next.treatmentConfigOptions?.treatmentConfigTemplates ?? [];

	for (const nextTemplate of nextTemplates) {
		const previousTemplate = previousTemplates.find(
			(t) => t.name === nextTemplate.name,
		);

		// A template that did not exist before this save has no prior value to have moved from,
		// so nothing has changed for an already-enrolled cohort.
		if (!previousTemplate) {
			continue;
		}

		const before = effectiveThreshold(
			previousTemplate.disenrollmentDaysThreshold,
		);
		const after = effectiveThreshold(nextTemplate.disenrollmentDaysThreshold);

		if (before !== after) {
			return {
				templateName: nextTemplate.name,
				templateId: (nextTemplate as { _id?: string })._id,
				field: "TEMPLATE_DEFAULT",
				previousValue: before,
				nextValue: after,
			};
		}
	}

	// SIMS-9222: segments belong to a TEMPLATE — its own list, else the plan-level list a plan saved
	// before that change carries (readTemplateSegments). A segment with no override inherits ITS
	// template's threshold, so that is what a participant in that phase is really judged against.
	for (const nextTemplate of nextTemplates) {
		const previousTemplate = previousTemplates.find(
			(t) => t.name === nextTemplate.name,
		);
		if (!previousTemplate) {
			continue;
		}

		const previousSegments = readTemplateSegments(
			previous.treatmentConfig,
			previousTemplate,
		);
		const nextSegments = readTemplateSegments(
			next.treatmentConfig as Record<string, unknown> | undefined,
			nextTemplate,
		);

		for (let index = 0; index < nextSegments.length; index += 1) {
			const nextSegment = nextSegments[index];
			const previousSegment = findPreviousSegment(
				previousSegments,
				nextSegment,
			);

			// A segment added in this save has no prior value to have moved from.
			if (!previousSegment) {
				continue;
			}

			const before = effectiveThreshold(
				previousSegment.disenrollmentDaysThreshold ??
					previousTemplate.disenrollmentDaysThreshold,
			);
			const after = effectiveThreshold(
				nextSegment.disenrollmentDaysThreshold ??
					nextTemplate.disenrollmentDaysThreshold,
			);

			if (before !== after) {
				return {
					templateName: nextTemplate.name,
					templateId: (nextTemplate as { _id?: string })._id,
					field: "SEGMENT",
					segmentWeekRange: nextSegment.weekRange,
					...(previousSegment.segmentId && {
						segmentId: previousSegment.segmentId,
					}),
					segmentNumber: index + 1,
					previousValue: before,
					nextValue: after,
				};
			}
		}
	}

	return null;
};
